import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { 
  Search, 
  Users, 
  Building2, 
  Handshake,
  TrendingUp,
  Globe,
  Shield,
  Zap,
  Target,
  Briefcase,
  Network,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Mail,
  Phone,
} from 'lucide-react';

export function AboutPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isDarkMode } = useTheme();
  const overlayBg = isDarkMode ? 'rgba(6, 9, 18, 0.82)' : 'rgba(220, 232, 255, 0.45)';

  const capabilities = [
    {
      icon: Search,
      title: 'Smart Investor Discovery',
      description: 'Find the perfect investors with powerful filters. Search by location, industry, investment stage, check size, and more.',
    },
    {
      icon: Shield,
      title: 'Verified Contact Data',
      description: 'Access verified email addresses and phone numbers for curated investor matches. All data is sourced from public records and regularly updated.',
    },
    {
      icon: Users,
      title: 'Comprehensive Profiles',
      description: 'Detailed investor profiles including investment thesis, portfolio companies, past investments, and firm information.',
    },
    {
      icon: Target,
      title: 'Precision Matching',
      description: 'Our intelligent matching system connects you with investors who actively invest in your industry and stage.',
    },
    {
      icon: Globe,
      title: 'Global Coverage',
      description: 'Investors from 50+ cities worldwide, including San Francisco, New York, London, Tel Aviv, Singapore, and more.',
    },
    {
      icon: Zap,
      title: 'Instant Access',
      description: 'No waiting periods. Get immediate access to investor contact details and start reaching out today.',
    },
  ];

  const useCases = [
    {
      title: 'For Founders',
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600',
      description: 'The primary purpose of RaiseBeacon - helping founders find the right investors for their startup.',
      points: [
        'Find investors who invest in your industry',
        'Filter by stage, location, and check size',
        'Get direct contact information',
        'Track your outreach efforts',
        'Save investor lists for future rounds',
      ],
    },
    {
      title: 'For Investors',
      icon: Network,
      color: 'from-purple-500 to-purple-600',
      description: 'Investors can use RaiseBeacon to discover and connect with other investors for co-investment opportunities.',
      points: [
        'Find co-investors for larger deals',
        'Connect with angel investors in your region',
        'Discover emerging fund managers',
        'Build your investor network',
        'Source deal flow from other investors',
      ],
    },
    {
      title: 'For Service Providers',
      icon: Handshake,
      color: 'from-green-500 to-green-600',
      description: 'Law firms, consultancies, and other service providers can identify potential clients.',
      points: [
        'Identify growing startups that need services',
        'Connect with venture capital firms',
        'Find accelerators and incubators',
        'Target family offices',
        'Build your B2B client pipeline',
      ],
    },
    {
      title: 'For Job Seekers',
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600',
      description: 'Professionals looking to join startups or VC firms can use RaiseBeacon for research.',
      points: [
        'Research VC firms before interviews',
        'Find actively investing companies',
        'Identify portfolio companies hiring',
        'Network with investment professionals',
        'Stay updated on industry trends',
      ],
    },
  ];

  const stats = [
    { value: 'Curated', label: 'Investor Matches', description: 'VCs, angels, PE firms, and family offices' },
    { value: '$50B+', label: 'Available Capital', description: 'Total assets under management' },
    { value: '50+', label: 'Cities', description: 'Major investment hubs worldwide' },
    { value: '15+', label: 'Industries', description: 'From SaaS to Biotech' },
  ];

  return (
    <>
      <div className="min-h-screen relative">
      <SEO
        title="About RaiseBeacon - The Investor Database for Startups"
        description="RaiseBeacon helps founders find investors, investors connect with co-investors, and professionals discover opportunities in the startup ecosystem."
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-card/80 dark:bg-background/10 border border-border/60 dark:border-transparent rounded-full px-4 py-2 text-sm font-medium mb-6 text-foreground dark:text-white">
              <Zap className="w-4 h-4 text-amber-400" />
              <span>Connecting the startup ecosystem</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              What is <span className="text-[#2B7FFF]">RaiseBeacon</span>?
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground dark:text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              RaiseBeacon is the most comprehensive investor database for startups. 
              We connect founders with capital, investors with opportunities, 
              and professionals with the startup ecosystem.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => navigate('/search')}
                size="lg"
                className="bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25"
              >
                <Search className="w-5 h-5 mr-2" />
                Explore Investors
              </Button>
              
              {!isAuthenticated && (
                <Button
                  onClick={() => navigate('/pricing')}
                  size="lg"
                  className="bg-card/80 hover:bg-card dark:bg-background/10 dark:hover:bg-background/20 text-foreground dark:text-white border border-border dark:border-white/30 rounded-full px-8 py-6 text-lg backdrop-blur-sm"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl font-bold text-[#2B7FFF] mb-2">
                  {stat.value}
                </div>
                <div className="font-semibold text-foreground mb-1">{stat.label}</div>
                <div className="text-sm text-muted-foreground">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-24 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What RaiseBeacon Does
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful tools and verified data to help you navigate the startup investment landscape.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-[#2B7FFF]/10 rounded-xl flex items-center justify-center mb-6">
                  <cap.icon className="w-6 h-6 text-[#2B7FFF]" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{cap.title}</h3>
                <p className="text-muted-foreground">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Who Uses RaiseBeacon?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Beyond fundraising - discover how different professionals leverage RaiseBeacon.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-8 shadow-sm border border-border hover:shadow-lg transition-shadow"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${useCase.color} rounded-xl flex items-center justify-center mb-6`}>
                  <useCase.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{useCase.title}</h3>
                <p className="text-muted-foreground mb-6">{useCase.description}</p>
                <ul className="space-y-3">
                  {useCase.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              How RaiseBeacon Works
            </h2>
            <p className="text-lg text-muted-foreground">
              Simple, powerful, and designed for results.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Search & Filter',
                description: 'Use our advanced filters to find investors that match your specific criteria - industry, stage, location, and more.',
              },
              {
                step: '02',
                title: 'Access Contacts',
                description: 'Get verified email addresses and phone numbers. No gatekeepers, no intermediaries.',
              },
              {
                step: '03',
                title: 'Save to network',
                description: 'Save investors to your network with one click. Track stages from New to Qualified, Proposal, and Closed.',
              },
              {
                step: '04',
                title: 'Connect & Close',
                description: 'Reach out directly, manage your pipeline, build relationships, and secure the funding you need to grow.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-6xl font-bold text-[#2B7FFF]/40 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials / Social Proof */}
      <section className="py-24 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Built for Real Founders
            </h2>
            <p className="text-lg text-muted-foreground">
              Join founders who found their perfect investor match through RaiseBeacon.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "RaiseBeacon helped us find the perfect seed investors for our SaaS startup. The contact data is gold.",
                author: "Founder, Tech Startup",
                role: "Series A",
              },
              {
                quote: "As an angel investor, I use RaiseBeacon to find co-investors. It's become an essential tool for deal sourcing.",
                author: "Angel Investor",
                role: "San Francisco Bay Area",
              },
              {
                quote: "The verified contact info saved us weeks of research. Worth every penny.",
                author: "CEO",
                role: "Healthcare Startup",
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card border border-border rounded-2xl p-8 shadow-sm"
              >
                <div className="text-[#2B7FFF] text-4xl font-serif mb-4">"</div>
                <p className="text-foreground mb-6">{testimonial.quote}</p>
                <div>
                  <div className="font-semibold">{testimonial.author}</div>
                  <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Coverage */}
      <section className="py-24 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Comprehensive Data Coverage
              </h2>
              <p className="text-muted-foreground dark:text-slate-300 text-lg mb-8">
                Our database includes detailed profiles on thousands of investors, 
                with verified contact information and investment history.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Building2, label: 'VC Firms', desc: 'Top-tier and emerging venture capital firms' },
                  { icon: Users, label: 'Angel Investors', desc: 'Individual investors and angel groups' },
                  { icon: Briefcase, label: 'Family Offices', desc: 'Private wealth management firms' },
                  { icon: BarChart3, label: 'Private Equity', desc: 'Growth equity and buyout firms' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#2B7FFF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#2B7FFF]" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">{item.label}</div>
                      <div className="text-sm text-muted-foreground dark:text-slate-400">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border dark:bg-background/5 dark:border-transparent rounded-3xl p-8">
              <h3 className="text-xl font-semibold text-foreground mb-6">What's Included in Each Profile</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Full Name & Title',
                  'Firm/Organization',
                  'Verified Email',
                  'Phone Number',
                  'Investment Thesis',
                  'Portfolio Companies',
                  'Check Size Range',
                  'Preferred Industries',
                  'Investment Stage',
                  'LinkedIn Profile',
                  'Geographic Focus',
                  'Recent Investments',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-foreground dark:text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#2563eb] to-[#1d4ed8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to explore RaiseBeacon?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join thousands of founders, investors, and professionals who are already using RaiseBeacon to navigate the startup ecosystem.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/search')}
              size="lg"
              className="bg-background text-blue-600 hover:bg-blue-50 rounded-full px-10 py-7 text-lg font-semibold shadow-xl transition-all duration-300 hover:scale-105"
            >
              <Search className="w-5 h-5 mr-2" />
              Search Investors
            </Button>
            {!isAuthenticated && (
              <Button
                onClick={() => navigate('/pricing')}
                size="lg"
                variant="outline"
                className="bg-background/10 border-white text-white hover:bg-background/20 rounded-full px-10 py-7 text-lg font-semibold"
              >
                View Pricing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#2B7FFF] rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-white text-xl font-bold">backr</span>
              </div>
              <p className="text-sm">
                Connecting founders with investors. The most comprehensive investor database for startups.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/search')} className="hover:text-white transition-colors">Search Investors</button></li>
                <li><button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">Pricing</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  support@backr.us
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">
              © 2025 RaiseBeacon. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Data provided for informational purposes only.
            </p>
          </div>
        </div>
      </footer>
    </div>
  </>
  );
}

